<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from "vue";
import { trackReportFeature } from "@/analytics/report-analytics";
import { trackDebounced } from "@/analytics/track-debounce";
import { useDebounceFn } from "@vueuse/core";
import { useEditor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";

const props = defineProps<{
  modelValue: string | null | undefined;
  title: string;
  icon?: string;
  previousReportContent?: string | null;
  previousReportLabel: string;
  editorPlaceholder: string;
  hint: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "save", value: string): void;
}>();

const debouncedSave = useDebounceFn(() => {
  const html = editor.value?.getHTML() ?? "";
  trackDebounced("report_text_content", () =>
    trackReportFeature("text_content_edited"),
  );
  emit("save", html);
}, 600);

const editor = useEditor({
  extensions: [
    StarterKit.configure({
      heading: { levels: [2, 3] },
    }),
    Underline,
  ],
  content: props.modelValue || "",
  editorProps: {
    attributes: {
      class: "tiptap-editor-content",
    },
  },
  onUpdate: () => {
    const html = editor.value?.getHTML() ?? "";
    emit("update:modelValue", html);
    debouncedSave();
  },
});

// Sync external changes (e.g. AI generation) into the editor
watch(
  () => props.modelValue,
  (newVal) => {
    if (!editor.value) return;
    // Ne pas écraser le contenu pendant que l'utilisateur écrit : ce watch ne
    // sert qu'à injecter des changements externes (ex : génération IA). Sinon,
    // le refetch déclenché par l'auto-save renvoie une valeur périmée qui
    // écrase les caractères tapés depuis l'envoi de la sauvegarde.
    if (editor.value.isFocused) return;
    const current = editor.value.getHTML();
    const incoming = newVal || "";
    if (incoming !== current) {
      editor.value.commands.setContent(incoming, { emitUpdate: false });
    }
  },
);

onBeforeUnmount(() => {
  editor.value?.destroy();
});

const hasPreviousContent = computed(
  () => !!props.previousReportContent?.trim(),
);
const isPreviousExpanded = ref(false);

// Toolbar helpers
const isActive = (name: string, attrs?: Record<string, unknown>) =>
  editor.value?.isActive(name, attrs) ?? false;
</script>

<template>
  <el-card shadow="never" class="text-content-card">
    <template #header>
      <div class="card-header-content">
        <i v-if="icon" :class="icon" class="card-header-icon" />
        <span class="card-header-title">{{ title }}</span>
      </div>
    </template>

    <div class="card-body">
      <!-- Previous report expandable -->
      <div v-if="hasPreviousContent" class="previous-report-section">
        <button
          type="button"
          class="previous-toggle"
          @click="isPreviousExpanded = !isPreviousExpanded"
        >
          <i
            class="bi previous-chevron"
            :class="isPreviousExpanded ? 'bi-chevron-down' : 'bi-chevron-right'"
          />
          <span>{{ previousReportLabel }}</span>
        </button>

        <div v-show="isPreviousExpanded" class="previous-content">
          {{ previousReportContent }}
        </div>
      </div>

      <!-- Tiptap toolbar + editor -->
      <div v-if="editor" class="tiptap-wrapper">
        <div class="tiptap-toolbar">
          <button
            type="button"
            :class="{ active: isActive('bold') }"
            @click="editor!.chain().focus().toggleBold().run()"
          >
            <i class="bi-type-bold" />
          </button>
          <button
            type="button"
            :class="{ active: isActive('italic') }"
            @click="editor!.chain().focus().toggleItalic().run()"
          >
            <i class="bi-type-italic" />
          </button>
          <button
            type="button"
            :class="{ active: isActive('underline') }"
            @click="editor!.chain().focus().toggleUnderline().run()"
          >
            <i class="bi-type-underline" />
          </button>

          <span class="toolbar-divider" />

          <button
            type="button"
            :class="{ active: isActive('heading', { level: 2 }) }"
            @click="editor!.chain().focus().toggleHeading({ level: 2 }).run()"
          >
            H2
          </button>
          <button
            type="button"
            :class="{ active: isActive('heading', { level: 3 }) }"
            @click="editor!.chain().focus().toggleHeading({ level: 3 }).run()"
          >
            H3
          </button>

          <span class="toolbar-divider" />

          <button
            type="button"
            :class="{ active: isActive('bulletList') }"
            @click="editor!.chain().focus().toggleBulletList().run()"
          >
            <i class="bi-list-ul" />
          </button>
          <button
            type="button"
            :class="{ active: isActive('orderedList') }"
            @click="editor!.chain().focus().toggleOrderedList().run()"
          >
            <i class="bi-list-ol" />
          </button>
        </div>

        <EditorContent :editor="editor" />
      </div>

      <div class="hint">
        <i class="bi-info-circle me-1" />
        {{ hint }}
      </div>
    </div>
  </el-card>
</template>

<style scoped lang="scss">
.text-content-card {
  background-color: #ffffff;
  border-color: var(--el-border-color-lighter);
  border-radius: 12px;
  width: 100%;

  :deep(.el-card__header) {
    padding: 16px 20px;
    border-bottom: 1px solid var(--el-border-color-lighter);
  }

  :deep(.el-card__body) {
    padding: 20px;
  }
}

.card-header-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-header-icon {
  font-size: 18px;
  color: var(--el-color-primary);
}

.card-header-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.previous-report-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.previous-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 0;
  background: transparent;
  border: none;
  color: var(--el-text-color-secondary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: var(--el-text-color-primary);
  }

  .previous-chevron {
    font-size: 11px;
  }
}

.previous-content {
  white-space: pre-wrap;
  color: var(--el-text-color-secondary);
  font-size: 12px;
  line-height: 1.5;
  max-height: 180px;
  overflow-y: auto;
  padding: 10px 12px;
  background-color: var(--el-fill-color-lighter);
  border-left: 2px solid var(--el-border-color);
  border-radius: 4px;
}

.hint {
  color: var(--el-text-color-secondary);
  font-size: 12px;
}

/* Tiptap wrapper */
.tiptap-wrapper {
  border: 1px solid var(--el-border-color);
  border-radius: 8px;
  overflow: hidden;
}

.tiptap-toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background-color: var(--el-fill-color-lighter);
  border-bottom: 1px solid var(--el-border-color);

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 28px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: var(--el-text-color-regular);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.1s ease;

    &:hover {
      background-color: var(--el-fill-color);
    }

    &.active {
      background-color: var(--el-color-primary-light-8);
      color: var(--el-color-primary);
    }
  }
}

.toolbar-divider {
  width: 1px;
  height: 18px;
  margin: 0 4px;
  background-color: var(--el-border-color);
}

/* Tiptap editor content */
:deep(.tiptap-editor-content) {
  min-height: 250px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--el-text-color-primary);
  outline: none;

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }

  h2 {
    font-size: 18px;
    font-weight: 700;
    margin: 16px 0 8px;
  }

  h3 {
    font-size: 15px;
    font-weight: 600;
    margin: 12px 0 6px;
  }

  p {
    margin: 0 0 6px;
  }

  ul,
  ol {
    padding-left: 20px;
    margin: 6px 0;
  }

  li {
    margin: 2px 0;
  }

  p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: var(--el-text-color-placeholder);
    pointer-events: none;
    height: 0;
  }
}
</style>
